import { Button, Input } from "@aix/ui"
import { X, Plus, Trash2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import dayjs from "dayjs"
import { trpc } from "../../../lib/trpc"

interface ManualPlanCreationModalProps {
  coachId: string
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function ManualPlanCreationModal({ coachId, isOpen, onClose, onSuccess }: ManualPlanCreationModalProps) {
  const [weekStartDate, setWeekStartDate] = useState(dayjs().startOf("week").format("YYYY-MM-DD"))
  const [weekEndDate, setWeekEndDate] = useState(dayjs().endOf("week").format("YYYY-MM-DD"))
  const [phaseName, setPhaseName] = useState("")
  const [expectations, setExpectations] = useState<string[]>([""])
  const [dailyStructure, setDailyStructure] = useState<Record<string, string>>({
    monday: "",
    tuesday: "",
    wednesday: "",
    thursday: "",
    friday: "",
    saturday: "",
    sunday: "",
  })

  const createMutation = trpc.plans.weeklyPlans.generate.useMutation({
    onSuccess: () => {
      toast.success("Manual plan created successfully!")
      onSuccess()
      onClose()
      // Reset form
      setWeekStartDate(dayjs().startOf("week").format("YYYY-MM-DD"))
      setWeekEndDate(dayjs().endOf("week").format("YYYY-MM-DD"))
      setPhaseName("")
      setExpectations([""])
      setDailyStructure({
        monday: "",
        tuesday: "",
        wednesday: "",
        thursday: "",
        friday: "",
        saturday: "",
        sunday: "",
      })
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create manual plan")
    },
  })

  const handleAddExpectation = () => {
    setExpectations([...expectations, ""])
  }

  const handleRemoveExpectation = (index: number) => {
    setExpectations(expectations.filter((_, i) => i !== index))
  }

  const handleExpectationChange = (index: number, value: string) => {
    const newExpectations = [...expectations]
    newExpectations[index] = value
    setExpectations(newExpectations)
  }

  const handleDailyStructureChange = (day: string, value: string) => {
    setDailyStructure({ ...dailyStructure, [day]: value })
  }

  const handleSubmit = () => {
    // Validate
    const validExpectations = expectations.filter((e) => e.trim().length > 0)
    if (validExpectations.length === 0) {
      toast.error("Please add at least one expectation")
      return
    }

    if (!weekStartDate || !weekEndDate) {
      toast.error("Please select week start and end dates")
      return
    }

    // Calculate week number
    const startDate = new Date(weekStartDate)
    const startOfYear = new Date(startDate.getFullYear(), 0, 1)
    const weekNumber = Math.ceil(
      ((startDate.getTime() - startOfYear.getTime()) / 86400000 + startOfYear.getDay() + 1) / 7,
    )

    // Filter out empty daily structure
    const filteredDailyStructure = Object.fromEntries(
      Object.entries(dailyStructure).filter(([_, value]) => value.trim().length > 0),
    )

    createMutation.mutate({
      coachId,
      weekNumber,
      weekStartDate,
      weekEndDate,
      phaseName: phaseName || undefined,
      // Note: The API expects this format, but we'll handle it in a custom way
      // For manual plans, we'd ideally want a different endpoint or flag
      context: JSON.stringify({
        manual: true,
        expectations: validExpectations,
        dailyStructure: Object.keys(filteredDailyStructure).length > 0 ? filteredDailyStructure : undefined,
      }),
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Create Manual Weekly Plan</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Manually define your expectations and daily structure for the week
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Week Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Week Start Date</label>
              <Input type="date" value={weekStartDate} onChange={(e) => setWeekStartDate(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Week End Date</label>
              <Input type="date" value={weekEndDate} onChange={(e) => setWeekEndDate(e.target.value)} />
            </div>
          </div>

          {/* Phase Name */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Phase Name (Optional)</label>
            <Input
              type="text"
              value={phaseName}
              onChange={(e) => setPhaseName(e.target.value)}
              placeholder="e.g., Foundation Phase, Growth Phase"
            />
          </div>

          {/* Expectations */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-foreground">Week Expectations</label>
              <Button onClick={handleAddExpectation} size="sm" variant="outline" className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add Expectation
              </Button>
            </div>
            <div className="space-y-3">
              {expectations.map((expectation, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    type="text"
                    value={expectation}
                    onChange={(e) => handleExpectationChange(index, e.target.value)}
                    placeholder={`Expectation ${index + 1}`}
                    className="flex-1"
                  />
                  {expectations.length > 1 && (
                    <Button
                      onClick={() => handleRemoveExpectation(index)}
                      size="sm"
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Daily Structure */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">Daily Structure (Optional)</label>
            <div className="space-y-3">
              {Object.keys(dailyStructure).map((day) => (
                <div key={day}>
                  <label className="block text-xs text-muted-foreground mb-1 capitalize">{day}</label>
                  <textarea
                    value={dailyStructure[day]}
                    onChange={(e) => handleDailyStructureChange(day, e.target.value)}
                    placeholder={`What should happen on ${day}?`}
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-primary resize-none"
                    rows={2}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={createMutation.isLoading} disabled={createMutation.isLoading}>
            Create Plan
          </Button>
        </div>
      </div>
    </div>
  )
}
